import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useBrand } from "../../context/SiteSettingsContext";
import BrandLogo from "../shared/BrandLogo";
import { SITE_SETTINGS } from "../../data/site";
import { useServices } from "../../context/ServicesContext";
import { generateWhatsAppUrl } from "../../lib/whatsapp";
import { cn } from "../../lib/utils";

export default function MobileMenu({ open, onClose }) {
  const BRAND = useBrand();
  const { services } = useServices();
  const [servicesOpen, setServicesOpen] = useState(false);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-50 bg-ink text-white overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="container-emilio flex items-center justify-between h-20">
            <Link to="/" onClick={onClose} className="flex items-center gap-2">
              <BrandLogo className="h-9" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              data-testid="mobile-menu-close"
              className="w-10 h-10 border border-charcoal grid place-items-center hover:border-emerald hover:text-emerald transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="rule-green opacity-30" />

          <nav className="container-emilio py-8 flex flex-col gap-1">
            {SITE_SETTINGS.navigation.map((item) => {
              if (item.hasMega) {
                return (
                  <div key={item.to} className="border-b border-charcoal/60">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between py-4 text-lg font-display font-semibold text-white"
                      onClick={() => setServicesOpen((v) => !v)}
                      data-testid="mobile-services-toggle"
                    >
                      Our Services
                      <ChevronDown
                        className={cn(
                          "w-5 h-5 transition-transform duration-300",
                          servicesOpen && "rotate-180"
                        )}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {servicesOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-1 pb-4">
                            {services.map((s, i) => (
                              <Link
                                key={s.slug}
                                to={s.route}
                                onClick={onClose}
                                className="flex items-center gap-3 py-3 text-sm text-white/80 hover:text-emerald"
                                data-testid={`mobile-service-${s.slug}`}
                              >
                                <span className="text-emerald font-mono text-xs w-8">
                                  {String(i + 1).padStart(2, "0")}
                                </span>
                                {s.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      "py-4 border-b border-charcoal/60 text-lg font-display font-semibold",
                      isActive ? "text-emerald" : "text-white"
                    )
                  }
                  data-testid={`mobile-nav-${item.to.replace(/\//g, "-") || "home"}`}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="container-emilio pb-10">
            <a
              href={generateWhatsAppUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald text-ink px-5 py-4 text-base font-semibold"
              data-testid="mobile-menu-whatsapp"
            >
              Book via WhatsApp
            </a>
            <div className="mt-6 text-sm text-mutedtext">
              <div>{BRAND.phone}</div>
              <div>{BRAND.email}</div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
